import Typography from '@mui/material/Typography';
import { FeatureFlag } from '@ps/web-core-enums';
import { type Dispatch, useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { buildIncodePayload } from 'packages/swf-verification/IncodeProvider/utils';
import SuccessIcon from '@swf/core/assets/icons/success-deposit.svg';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import useWizardLoading from '@swf/core/hooks/useWizardLoading';
import { select } from '@swf/core/store';
import AccountVerification from '@swf/verification/AccountVerification/AccountVerification';
import type { OcrData, ScoreResponse } from '@swf/verification/IncodeProvider/Incode/incode_types';

import CTAButton from '../../../../components/CTAButton/CTAButton';
import FirstLoader from '../../../../components/FirstLoader/FirstLoader';
import { CashierFlowType } from '../../../../types/Cashier';
import Container from '../../../Container/Container';
import HelpModal from '../../../HelpModal/HelpModal';

import {
  TXTCLI_Okay,
  TXTWCL_Account_declined,
  TXTWCL_Please_try_again_or_return_lat_ELL,
  TXTWCL_Something_went_wrong,
  TXTWCL_SorryX_the_document_verificati_ELL,
  TXTWCL_Try_again,
  TXTWCL_WeXre_working_on_this,
  TXTWCL_Your_details_have_been_verifie_ELL,
  TXTWCL_Your_documents_4
} from './RMFVerificationUSPA.messages.json';
import { align, icon, wrapper } from './RMFVerificationUSPA.scss';

const RMFVerificationUSPA: React.FC = () => {
  const {
    data: { cashierFlow }
  } = useWizardContext();
  const exitWizard = useWizardExit();
  const { jumpBack } = useWizardJump();
  const [panelVisible, setPanelVisible] = useState(true);
  const isStandaloneRmf = useSelector(select.location.isStandAloneRMF);
  const [userScores, setUserScores] = useState<ScoreResponse>();
  const [ocrData, setOcrData] = useState<OcrData>();
  const { loading } = useWizardLoading();
  const isSwcIncodeAllowFailedResult = useFeature(FeatureFlag.SwcIncodeAllowFailedResult);
  const userDOB = useSelector(select.account.getDateOfBirth);
  const [incodeError, setIncodeError] = useState(false);
  const [incodeRestartFlow, setIncodeRestartFlow] = useState(false);
  const isDepositFlow = cashierFlow === CashierFlowType.FirstDeposit;

  const {
    account: { updateAccount }
  } = useDispatch<Dispatch>();

  const handleIncodeError = () => {
    setPanelVisible(false);
    setIncodeError(true);
  };

  const handleClose = () => {
    !isStandaloneRmf && exitWizard();
  };

  const updateWithIncodeDetails = useCallback(
    async (data: OcrData) => {
      const payload = buildIncodePayload(data, userDOB);
      await updateAccount(payload);
      setOcrData(data);
      setPanelVisible(false);
    },
    [updateAccount, userDOB]
  );

  const handleIncodeComplete = async (data?: OcrData, score?: ScoreResponse) => {
    if (data && score) {
      setUserScores(score);

      if (
        score.overall.status === 'OK' ||
        (isSwcIncodeAllowFailedResult && score.overall.status === 'FAIL')
      ) {
        updateWithIncodeDetails(data);
      } else {
        setPanelVisible(false);
      }
    }
  };

  const handleBack = () => {
    setPanelVisible(false);
    jumpBack('#rmf.rmf-phone');
  };

  return (
    <Container
      page={RMFVerificationUSPA}
      headerProps={{
        title: !loading && <Translation {...TXTWCL_Your_documents_4} />,
        hideProgressBar: true
      }}
    >
      {!loading && (
        <HelpModal
          visible={panelVisible}
          onClose={handleBack}
          title={TXTWCL_Your_documents_4}
          hideProgressBar
        >
          <AccountVerification
            openIDScan
            showButton
            onComplete={handleIncodeComplete}
            onClose={handleClose}
            onError={handleIncodeError}
            onRestart={() => setIncodeRestartFlow(true)}
            handleCancel={() => setPanelVisible(false)}
          />
        </HelpModal>
      )}
      {loading && <FirstLoader label={TXTWCL_WeXre_working_on_this} />}
      {userScores &&
        !loading &&
        !incodeRestartFlow &&
        (ocrData ? (
          <>
            <div className={`${wrapper} ${isDepositFlow || isStandaloneRmf ? align : ''}`}>
              <SuccessIcon className={icon} />
              <Translation tagName="h2" {...TXTWCL_Your_details_have_been_verifie_ELL} />
            </div>
            <CTAButton />
          </>
        ) : (
          <>
            <div>
              <Translation tagName="h2" {...TXTWCL_Account_declined} />
              <Typography>
                <Translation {...TXTWCL_SorryX_the_document_verificati_ELL} />
              </Typography>
            </div>
            <CTAButton translation={TXTCLI_Okay} onClick={exitWizard} />
          </>
        ))}
      {incodeError && (
        <>
          <div>
            <Translation tagName="h2" {...TXTWCL_Something_went_wrong} />
            <Typography>
              <Translation {...TXTWCL_Please_try_again_or_return_lat_ELL} />
            </Typography>
          </div>
          <CTAButton translation={TXTWCL_Try_again} onClick={exitWizard} />
        </>
      )}
    </Container>
  );
};

RMFVerificationUSPA.displayName = 'RMFVerificationUSPA';

export default RMFVerificationUSPA;
